import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router';
import { errorLoadingMsg, loadingFormMsg, t } from '../../../common/i18n/index.js';
import { OpenFormAnswers } from '../OpenFormAnswers.js';
import { OpenFormAsync } from '../OpenFormAsync.js';
import { useOpenFormAsync } from '../OpenFormProvider.js';
import { OpenFormSnapshotEvent } from '../OpenFormSnapshotEvent.js';
import { getValue } from '../OpenFormUtils.js';
import { type OpenFormGotoProps } from './useOpenFormGoto.js';
import { setValueOptions } from './useOpenFormMethods.js';
import type { OpenFormDraft } from '../../../generated/api/openFormDraft.js';
import type { OpenFormDraftItem } from '../../../generated/api/openFormDraftItem.js';
import type { OpenFormQuestion } from '../../../generated/api/openFormQuestion.js';
import type { OpenFormResponse } from '../../../generated/api/openFormResponse.js';

export class OpenForm {
  constructor(
    private readonly response: OpenFormResponse,
    readonly reference = response.formId,
    readonly questions = response.sections.flatMap(s => s.questions)
  ) {}

  get formId() {
    return this.response.formId;
  }

  get name() {
    return this.response.name;
  }

  get referenceNumber() {
    return this.response.formId !== this.reference ? this.reference : undefined;
  }

  get sections() {
    return this.response.sections;
  }

  linkedValues(q: OpenFormQuestion, value: string | string[] | undefined) {
    return value?.length ? q.choices.flatMap(c => (value.includes(c.guid) && c.linkedValue) || []) : [];
  }

  postDraft(draftState = OpenFormAsync.store.getItem(this.reference)!) {
    return OpenFormAsync.fetch.postOpenFormDraft({ draftState, formId: this.formId, referenceNumber: this.reference });
  }

  restore(state = JSON.parse(OpenFormAsync.store.getItem(this.reference)!)) {
    return { answers: new OpenFormAnswers(state?.answers && Object.entries(state.answers)), page: state?.page };
  }

  snapshot({ answers, page } = OpenFormAsync.state, store = OpenFormAsync.store) {
    answers.size
      ? store.setItem(this.reference, JSON.stringify({ answers: Object.fromEntries(answers), page }))
      : store.removeItem(this.reference);
    self.dispatchEvent(new OpenFormSnapshotEvent(store.storage, store.namespace, this.reference));
  }
}

export const useOpenForm = (preview: boolean, formId: string) => {
  const [ready, setReady] = useState(false);
  const { reset, setValue, trigger } = useFormContext();
  const async = useOpenFormAsync();
  const state = useRef<OpenFormGotoProps>(useLocation().state);

  useEffect(() => {
    const { controller } = async.fetch
      .getOpenForm({ formId, referenceNumber: state.current?.referenceNumber }, preview)
      .resolved(([response, draft]) => {
        if (!draft) {
          async.flags.delete('draft') && async.draft.clear();
          if (state.current?.referenceNumber) {
            throw t.LOV4(errorLoadingMsg, state.current.referenceNumber);
          }
        } else if (!state.current?.referenceNumber) {
          async.draft.set(draft.referenceNumber, { ...draft, formId } as OpenFormDraftItem);
        } else {
          const { draftState, ...item } = draft as OpenFormDraft;
          async.draft.set(draft.referenceNumber, item);
          async.store.setItem(draft.referenceNumber, draftState);
        }
        const form = new OpenForm(response, draft?.referenceNumber);
        const { answers, page } = form.restore();
        answers.forEachOf('choices', ({ choices }, question) => setValue(question, getValue(choices), setValueOptions));
        trigger().then(() => async.actions.answers(answers).page(page).form(form));
      })
      .rejected(text => async.actions.notification({ text, type: 'error' }))
      .settled(() => setReady(true))
      .execute({ disable: true, message: t.DR5T(loadingFormMsg) });

    return () => {
      controller.abort();
      async.cache.clear();
      async.actions.form(undefined).answers(undefined).page(undefined);
      reset({});
    };
  }, [preview, formId, reset, setValue, trigger, async, state]);

  return ready;
};
